// A small, strict parser for the common subset of OSM opening_hours that UK
// pubs use: weekday ranges and lists, PH (bank holidays, per nation), times
// past midnight, 24/7 and off/closed, with later rules overriding earlier
// ones for the days they name. Anything else (months, dates, week numbers,
// sunrise, open ends, "||" fallbacks, school holidays) makes the whole tag
// unparseable, and the app says "Opening hours unknown" rather than guess.
//
// Written in-house rather than using opening_hours.js (LGPL-3.0, ~5 MB with
// i18n) so the bundle stays small and MIT-only; see CHANGELOG for the choice.
//
// No imports, so the data pipeline (scripts/) can use it too: it precomputes
// a weekly open/closed mask for the map index (weeklyMask / encodeMask).

/** An open interval in minutes from that day's midnight; `end` may run past 1440. */
export type Interval = readonly [start: number, end: number];

export interface OpeningSchedule {
  /** Monday = 0 … Sunday = 6. */
  week: Interval[][];
  /** Bank holiday hours, when the tag has a PH rule; otherwise the weekday applies. */
  holiday: Interval[] | null;
}

const DAY_NAMES: Record<string, number> = {
  mo: 0,
  mon: 0,
  monday: 0,
  tu: 1,
  tue: 1,
  tues: 1,
  tuesday: 1,
  we: 2,
  wed: 2,
  wednesday: 2,
  th: 3,
  thu: 3,
  thur: 3,
  thurs: 3,
  thursday: 3,
  fr: 4,
  fri: 4,
  friday: 4,
  sa: 5,
  sat: 5,
  saturday: 5,
  su: 6,
  sun: 6,
  sunday: 6,
};

const HOLIDAY = 7;
const DAY_TOKEN = /^[a-z]{2,9}$/i;

/** "Mo-Fr,Su,PH" → day indices (7 = bank holiday), or null if it isn't a day selector. */
function parseDays(token: string): number[] | null {
  const days: number[] = [];
  for (const part of token.split(',')) {
    const [a, b, ...rest] = part.split('-');
    if (a === undefined || rest.length > 0) return null;
    if (b === undefined) {
      if (/^ph$/i.test(a)) {
        days.push(HOLIDAY);
        continue;
      }
      const d = DAY_NAMES[a.toLowerCase()];
      if (d === undefined) return null;
      days.push(d);
      continue;
    }
    const from = DAY_NAMES[a.toLowerCase()];
    const to = DAY_NAMES[b.toLowerCase()];
    if (from === undefined || to === undefined) return null;
    // Ranges can wrap the week: Fr-Mo is Fri, Sat, Sun, Mon.
    for (let d = from; ; d = (d + 1) % 7) {
      days.push(d);
      if (d === to) break;
    }
  }
  return days;
}

const TIME = /^(\d{1,2}):(\d{2})$/;

function minutes(text: string): number | null {
  const m = TIME.exec(text);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 48 || min > 59) return null;
  return h * 60 + min;
}

/** "12:00-23:00,17:00-01:00" → intervals, or null. */
function parseTimes(token: string): Interval[] | null {
  const intervals: Interval[] = [];
  for (const span of token.split(',')) {
    const [a, b, ...rest] = span.split('-');
    if (a === undefined || b === undefined || rest.length > 0) return null;
    const start = minutes(a);
    let end = minutes(b);
    if (start === null || end === null || start >= 1440) return null;
    if (end <= start) end += 1440; // closes after midnight
    if (end - start > 1440) return null;
    intervals.push([start, end]);
  }
  return intervals;
}

/** Tidy the common hand-typed variations before parsing. */
function normalise(raw: string): string {
  return raw
    .replace(/[–—]/g, '-')
    .replace(/"[^"]*"/g, ' ') // comments
    .replace(/\s*-\s*/g, '-')
    .replace(/\s*,\s*/g, ',')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/;$/, '');
}

/** Parse an opening_hours value, or null if it's missing or outside the subset we trust. */
export function parseOpeningHours(raw: string | null | undefined): OpeningSchedule | null {
  if (!raw) return null;
  const text = normalise(raw);
  if (!text || text.includes('||')) return null;

  const all = (intervals: Interval[]) => Array.from({ length: 7 }, () => [...intervals]);
  if (text === '24/7') return { week: all([[0, 1440]]), holiday: null };

  const week: Interval[][] = all([]);
  let holiday: Interval[] | null = null;
  let anyRule = false;

  // "Mo-Fr 12:00-23:00, Sa 11:00-23:00": a comma after a time (or off) and
  // before a day name starts a new rule; "Sa,Su off" is still one day list.
  const rules = text
    .replace(/(\d|\boff|\bclosed),(?=[A-Za-z])/gi, '$1;')
    .split(';')
    .map((r) => r.trim())
    .filter(Boolean);

  for (const rule of rules) {
    const tokens = rule.split(' ');
    let days: number[] = [0, 1, 2, 3, 4, 5, 6];
    let rest = tokens;
    const first = tokens[0] ?? '';
    if (DAY_TOKEN.test(first.split(/[,-]/)[0] ?? '') && !/^(off|closed)$/i.test(first)) {
      const parsed = parseDays(first);
      if (!parsed) return null;
      days = parsed;
      rest = tokens.slice(1);
    }
    if (rest.length !== 1) return null;
    const spec = rest[0] ?? '';
    let intervals: Interval[];
    if (/^(off|closed)$/i.test(spec)) intervals = [];
    else {
      const parsed = parseTimes(spec);
      if (!parsed) return null;
      intervals = parsed;
    }
    for (const d of days) {
      if (d === HOLIDAY) holiday = [...intervals];
      else week[d] = [...intervals];
    }
    anyRule = true;
  }
  if (!anyRule || week.every((day) => day.length === 0)) return null;
  return { week, holiday };
}

// ---------------------------------------------------------------------------
// Weekly mask: one bit per hour for Monday–Sunday (168 bits), plus a bank
// holiday day (24 more bits) when the tag has a PH rule. An hour is open when
// the pub is open for at least half of it. Stored base64 in the map index.

export const HOURS_PER_WEEK = 168;
const HOLIDAY_DAY = 7;

/** Minutes open within [from, to) of a day, given that day's and the previous day's intervals. */
export function minutesOpen(
  today: readonly Interval[],
  yesterday: readonly Interval[],
  from: number,
  to: number,
): number {
  let total = 0;
  const overlap = (a: number, b: number) => Math.max(0, Math.min(b, to) - Math.max(a, from));
  for (const [a, b] of today) total += overlap(a, b);
  // Yesterday's late opening spills past midnight.
  for (const [a, b] of yesterday) if (b > 1440) total += overlap(a - 1440, b - 1440);
  return Math.min(to - from, total);
}

/** The weekly mask as bytes (21, or 24 with a bank holiday day). */
export function weeklyMask(schedule: OpeningSchedule): Uint8Array {
  const days = schedule.holiday ? 8 : 7;
  const bytes = new Uint8Array(days * 3);
  for (let d = 0; d < days; d++) {
    const today = d === HOLIDAY_DAY ? (schedule.holiday ?? []) : (schedule.week[d] ?? []);
    // A bank holiday's spill-over from the day before isn't known: treat it as none.
    const yesterday = d === HOLIDAY_DAY ? [] : (schedule.week[(d + 6) % 7] ?? []);
    for (let h = 0; h < 24; h++) {
      if (minutesOpen(today, yesterday, h * 60, h * 60 + 60) >= 30) {
        const bit = d * 24 + h;
        bytes[bit >> 3] = (bytes[bit >> 3] ?? 0) | (1 << (bit & 7));
      }
    }
  }
  return bytes;
}

export function encodeMask(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

export function decodeMask(text: string): Uint8Array | null {
  try {
    const binary = atob(text);
    if (binary.length !== 21 && binary.length !== 24) return null;
    return Uint8Array.from(binary, (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
}

/**
 * Open in this hour? `weekday` is 0 = Monday … 6 = Sunday; on a bank
 * holiday the PH day is used when the mask has one.
 */
export function maskOpen(
  mask: Uint8Array,
  weekday: number,
  hour: number,
  holiday: boolean,
): boolean {
  const day = holiday && mask.length === 24 ? HOLIDAY_DAY : weekday;
  const bit = day * 24 + hour;
  return ((mask[bit >> 3] ?? 0) & (1 << (bit & 7))) !== 0;
}
